import { Button, Heading, Stack, Text, useToast } from "@chakra-ui/react";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import useApiRequest from "hooks/api/useApiHook";
import { FC, useEffect, useState } from "react";
import { TRANSFER_MIN_USD, formatUsd, serverErrorMessage } from "./helpers";

type Props = {
  apiToken: string;
  // Available referral balance (ready_to_withdraw) from the stats card.
  available: number;
  // Bump the parent's refresh counters so stats/balance re-fetch after a move.
  onTransferred: () => void;
};

// "Transfer to balance" — moves the FULL available referral balance to the
// main balance in one server-side operation. The client never sends an amount
// (US2 NEVER); the button just fires the empty-body POST.
const TransferCard: FC<Props> = ({ apiToken, available, onTransferred }) => {
  const toast = useToast();

  // Stable-identity trigger held in state: undefined = idle (no request),
  // a fresh object = fire once. Replacing it re-fires (see useApiHook).
  const [trigger, setTrigger] = useState<{ submit: boolean } | undefined>(
    undefined
  );

  const { data, error, loading } = useApiRequest({
    apiToken,
    method: RequestMethod.POST,
    name: RequestName.REFERRAL_TRANSFER,
    referralTransferParams: trigger,
  });

  useEffect(() => {
    if (data?.status === "success") {
      toast({
        title: `Transferred ${formatUsd(data.transferred_usd)} $ to your balance`,
        status: "success",
        duration: 3000,
      });
      setTrigger(undefined);
      onTransferred();
    }
    // onTransferred is stable (useCallback in parent); data is the real trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (error) {
      // Surface the server's rejection (e.g. below-minimum) instead of a no-op.
      toast({
        title: serverErrorMessage(error),
        status: "error",
        duration: 4000,
      });
      setTrigger(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const nothingToWithdraw = available <= 0;
  const belowMinimum = available > 0 && available < TRANSFER_MIN_USD;

  const onTransfer = () => {
    if (nothingToWithdraw || belowMinimum) return;
    setTrigger({ submit: true });
  };

  return (
    <BoxShadowContainer>
      <Stack spacing={3} width="100%">
        <Heading as="h3" size="sm">
          Transfer to balance
        </Heading>
        <Text color="text.secondary" fontSize={14}>
          Move your full referral balance to your main balance to spend it on
          IPScore.
        </Text>
        {nothingToWithdraw ? (
          <Text color="text.secondary">Nothing to withdraw yet.</Text>
        ) : belowMinimum ? (
          <Text color="text.secondary">
            You need at least {TRANSFER_MIN_USD.toFixed(2)} $ to transfer. Current
            referral balance: {formatUsd(available)} $.
          </Text>
        ) : (
          <Button
            colorScheme="green"
            onClick={onTransfer}
            isLoading={loading}
            alignSelf="flex-start"
          >
            Transfer {formatUsd(available)} $
          </Button>
        )}
      </Stack>
    </BoxShadowContainer>
  );
};

export default TransferCard;
